import React, { useState } from 'react';
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Link from '@mui/material/Link'
import { FormWrapper } from './Wrappers';
import { genericMemo } from './Types';

// The currencies a Swedish orienteering club realistically invoices in: SEK, the Nordics, and the
// handful of majors that turn up for foreign competitions. Visma accepts 36 codes in total; add
// one here if it is ever needed (the code is passed straight through, nothing else assumes a list).
export const CURRENCIES = ['SEK', 'NOK', 'DKK', 'EUR', 'ISK', 'GBP', 'CHF', 'USD', 'PLN', 'CZK'] as const

// A blank/absent currency means SEK everywhere in Eko — on the scan, on the supplier and on old
// drafts saved before currencies existed.
export const normalizeCurrency = (currency: string | null | undefined): string => currency || 'SEK'

export const isForeign = (currency: string | null | undefined): boolean => normalizeCurrency(currency) !== 'SEK'

function CurrencySelectRaw({onChange, ...props}: {onChange: (newValue: string) => void, id?: string}) {
  return (
    <Select {...props} onChange={(e) => {onChange(e.target.value as string)}}>
      {CURRENCIES.map(currency => <MenuItem key={currency} value={currency}>{currency}</MenuItem>)}
    </Select>
  )
}

const CurrencySelect = genericMemo(FormWrapper({Component: CurrencySelectRaw}))

// Three characters don't need a full-width field, and the vast majority of invoices are domestic:
// for SEK the picker collapses to a quiet link, so an ordinary invoice shows no currency chrome at
// all. A foreign invoice always shows the picker expanded — it can never hide behind the link,
// whether the currency came from the scan or from a saved draft.
//
// When foreign, the field is accented as a warning *and* keeps showing the code as text: colour
// alone would be invisible to a colour-blind user and can't say which currency it is.
export function CollapsibleCurrencySelect({id, label, value, onChange}: {
  id?: string, label?: string, value: string, onChange: (newValue: string) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const foreign = isForeign(value)

  if (!foreign && !expanded) {
    return (
      <div>
        <Link component="button" type="button" variant="body2" underline="hover"
              id={id === undefined ? undefined : `${id}-reveal`}
              sx={{color: 'text.secondary'}}
              onClick={() => setExpanded(true)}>
          Annan valuta
        </Link>
      </div>
    )
  }
  return (
    <CurrencySelect id={id} label={label} value={value} onChange={onChange}
      sx={{
        width: '7rem',
        ...(foreign ? {
          '& .MuiOutlinedInput-notchedOutline': {borderColor: 'warning.main', borderWidth: 2},
          '& .MuiSelect-outlined': {fontWeight: 700},
        } : {}),
      }}/>
  )
}

export default CurrencySelect
